'use client';
import React, { useState } from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { t } from '@/lib/i18n';
import { leaderboardData } from '@/lib/mockData';
import PodiumSection from './PodiumSection';
import LeaderboardTable from './LeaderboardTable';
import { Trophy } from 'lucide-react';

type TabType = 'weekly' | 'monthly' | 'global';

export default function LeaderboardContent() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>('weekly');

  const tabs: { key: TabType; label: string }[] = [
    { key: 'weekly', label: t(language, 'lb_weekly') },
    { key: 'monthly', label: t(language, 'lb_monthly') },
    { key: 'global', label: t(language, 'lb_global') },
  ];

  // Sort differently per tab to simulate different rankings
  const sortedData = [...leaderboardData].sort((a, b) => {
    if (activeTab === 'weekly') return b.weeklyXp - a.weeklyXp;
    if (activeTab === 'monthly') return b.xp * 0.3 + b.weeklyXp * 0.7 - (a.xp * 0.3 + a.weeklyXp * 0.7);
    return b.xp - a.xp;
  }).map((entry, i) => ({ ...entry, rank: i + 1 }));

  const currentUserEntry = sortedData.find((e) => e.isCurrentUser);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
          <Trophy size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-700 text-foreground">{t(language, 'lb_title')}</h1>
          <p className="text-sm text-muted-foreground">
            {language === 'fr' ?'Compétition hebdomadaire — mise à jour en temps réel' :'Еженедельное соревнование — обновление в реальном времени'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-card border border-border rounded-xl p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={`lb-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2 rounded-lg text-sm font-600 transition-all duration-150 ${
              activeTab === tab.key
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Podium */}
      <PodiumSection
        entries={sortedData.slice(0, 3)}
        language={language}
        activeTab={activeTab}
      />

      {/* Full table */}
      <LeaderboardTable
        entries={sortedData}
        language={language}
        activeTab={activeTab}
      />

      {/* Current user sticky footer */}
      {currentUserEntry && currentUserEntry.rank > 10 && (
        <div className="sticky bottom-4 bg-primary/10 border border-primary rounded-2xl p-4 flex items-center gap-4">
          <span className="text-lg font-800 text-primary font-tabular w-8 text-center">
            #{currentUserEntry.rank}
          </span>
          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white text-sm font-700">
            {currentUserEntry.avatar}
          </div>
          <div className="flex-1">
            <p className="text-sm font-700 text-foreground">{currentUserEntry.name}</p>
            <p className="text-xs text-muted-foreground">{t(language, 'lb_your_rank')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-700 text-primary font-tabular">
              {activeTab === 'weekly' ? currentUserEntry.weeklyXp : currentUserEntry.xp} XP
            </p>
            <p className="text-xs text-muted-foreground">🔥 {currentUserEntry.streak}</p>
          </div>
        </div>
      )}
    </div>
  );
}